import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

function Num({ label, value, onChange, testId, step = "1", min }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</Label>
      <Input type="number" step={step} min={min} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="rounded-none mt-1" data-testid={`${testId}-input`} />
    </div>
  );
}

function Txt({ label, value, onChange, testId, textarea, rows = 2 }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</Label>
      {textarea ? (
        <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1" data-testid={`${testId}-input`} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1" data-testid={`${testId}-input`} />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange, testId, hint }) {
  return (
    <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]" data-testid={testId}>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={`${testId}-switch`} />
      <span>{label}{hint && <span className="block text-xs text-taupe">{hint}</span>}</span>
    </label>
  );
}

const emptyBand = () => ({ max_sqm: 50, price: 0 });
const emptyTravelBand = () => ({ max_km: 50, cost: 0 });

export function EstimatorConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/estimator/config");
      setCfg(data);
    } catch { toast.error("Errore nel caricamento della configurazione"); }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <p className="text-sm text-taupe">Caricamento…</p>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const setFiscal = (k, v) => setCfg({ ...cfg, fiscal: { ...cfg.fiscal, [k]: v } });
  const setTravel = (k, v) => setCfg({ ...cfg, travel: { ...cfg.travel, [k]: v } });
  const setPkg = (i, k, v) => {
    const pkgs = cfg.packages.slice();
    pkgs[i] = { ...pkgs[i], [k]: v };
    setCfg({ ...cfg, packages: pkgs });
  };

  const save = async () => {
    if (!cfg.fiscal.configured && !window.confirm(
      "La configurazione fiscale non è contrassegnata come completata: il configuratore pubblico resterà disattivato. Salvare comunque?")) return;
    setBusy(true);
    try {
      const payload = {
        enabled: cfg.enabled, estimate_validity_days: cfg.estimate_validity_days,
        max_attachment_mb: cfg.max_attachment_mb, urgency_percent: cfg.urgency_percent,
        urgency_note: cfg.urgency_note, travel: cfg.travel, disclaimer: cfg.disclaimer,
        premise_text: cfg.premise_text, packages: cfg.packages, fiscal: cfg.fiscal,
      };
      const { data } = await api.put("/admin/estimator/config", payload);
      setCfg(data);
      toast.success("Configurazione salvata");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const active = cfg.enabled && cfg.fiscal?.configured;
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  return (
    <div className="space-y-6" data-testid="estimator-config-panel">
      <div className={`border p-4 md:p-5 flex items-start gap-3 ${active ? "bg-emerald-50 border-emerald-700/30" : "bg-amber-50 border-amber-700/40"}`} data-testid="estimator-status-banner">
        <AlertTriangle size={18} className={active ? "text-emerald-700 mt-0.5" : "text-amber-700 mt-0.5"} />
        <div>
          <p className="text-sm font-medium">{active ? "Il configuratore pubblico è attivo" : "Il configuratore pubblico è disattivato"}</p>
          <p className="text-xs text-taupe mt-1">
            Per attivarlo servono entrambe le condizioni: interruttore generale attivo e configurazione fiscale contrassegnata come completata.
          </p>
        </div>
      </div>

      <div className="bg-white border border-hair p-5 md:p-6 space-y-4">
        <div className="eyebrow">Parametri generali</div>
        <Toggle label="Configuratore abilitato" checked={cfg.enabled} onChange={(v) => set("enabled", v)}
          testId="cfg-enabled" hint="Se spento, la pagina /stima mostra un messaggio di non disponibilità" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Num label="Validità stima (giorni)" value={cfg.estimate_validity_days} onChange={(v) => set("estimate_validity_days", v)} testId="cfg-validity" min={1} />
          <Num label="Allegato max (MB)" value={cfg.max_attachment_mb} onChange={(v) => set("max_attachment_mb", v)} testId="cfg-attachment" min={1} />
          <Num label="Maggiorazione urgenza (%)" value={cfg.urgency_percent} onChange={(v) => set("urgency_percent", v)} testId="cfg-urgency-pct" step="0.5" min={0} />
        </div>
        <Txt label="Nota urgenza" value={cfg.urgency_note} onChange={(v) => set("urgency_note", v)} testId="cfg-urgency-note" />
        <Txt label="Premessa della stima (usa {nome} per il nome del cliente)" value={cfg.premise_text} onChange={(v) => set("premise_text", v)} testId="cfg-premise" textarea rows={6} />
        <Txt label="Disclaimer del documento" value={cfg.disclaimer} onChange={(v) => set("disclaimer", v)} testId="cfg-disclaimer" textarea rows={3} />
      </div>

      <div className="bg-white border border-hair p-5 md:p-6 space-y-4">
        <div className="eyebrow">Trasferte</div>
        <div className="space-y-2" data-testid="cfg-travel-bands">
          {(cfg.travel?.bands || []).map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid={`cfg-travel-band-${i}`}>
              <Num label={i === 0 ? "Fino a (km)" : "Fino a (km)"} value={b.max_km} min={1}
                onChange={(v) => { const bands = cfg.travel.bands.slice(); bands[i] = { ...b, max_km: v }; setTravel("bands", bands); }}
                testId={`cfg-travel-band-${i}-km`} />
              <Num label="Costo (€, 0 = inclusa)" value={b.cost} step="0.01" min={0}
                onChange={(v) => { const bands = cfg.travel.bands.slice(); bands[i] = { ...b, cost: v }; setTravel("bands", bands); }}
                testId={`cfg-travel-band-${i}-cost`} />
              <button onClick={() => setTravel("bands", cfg.travel.bands.filter((_, j) => j !== i))}
                className="min-h-[44px] px-3 border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone transition-colors"
                data-testid={`cfg-travel-band-${i}-remove`} aria-label="Rimuovi fascia"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setTravel("bands", [...(cfg.travel?.bands || []), emptyTravelBand()])}
            className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="cfg-travel-band-add">
            <Plus size={14} /> Aggiungi fascia km
          </button>
        </div>
        <Txt label="Nota oltre fascia massima" value={cfg.travel?.over_note} onChange={(v) => setTravel("over_note", v)} testId="cfg-travel-over-note" />
        <Txt label="Nota verifica distanza" value={cfg.travel?.verify_note} onChange={(v) => setTravel("verify_note", v)} testId="cfg-travel-verify-note" />
        <Txt label="Nota esclusioni trasferta" value={cfg.travel?.excluded_note} onChange={(v) => setTravel("excluded_note", v)} testId="cfg-travel-excluded-note" />
      </div>

      <div className="bg-white border border-hair p-5 md:p-6 space-y-5">
        <div className="eyebrow">Pacchetti e regole prezzi</div>
        <p className="text-xs text-taupe">I testi descrittivi (inclusioni, esclusioni, tempi) vengono dalla scheda del servizio collegato: modificali nella tab Servizi. Qui gestisci solo prezzi e visibilità nel configuratore.</p>
        {(cfg.packages || []).map((p, i) => (
          <div key={p.key} className="border border-hair p-4 md:p-5 space-y-4" data-testid={`cfg-package-${p.key}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-serif text-lg">{p.name}</p>
                <p className="text-xs text-taupe">{p.pricing === "flat" ? "Prezzo fisso" : "Prezzo a fasce di superficie"}{p.service_slug ? ` · servizio: ${p.service_slug}` : ""}</p>
              </div>
              <Toggle label="Visibile nel configuratore" checked={p.published} onChange={(v) => setPkg(i, "published", v)} testId={`cfg-package-${p.key}-published`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Txt label="Nome mostrato" value={p.name} onChange={(v) => setPkg(i, "name", v)} testId={`cfg-package-${p.key}-name`} />
              <Num label="Ordine" value={p.order} onChange={(v) => setPkg(i, "order", v)} testId={`cfg-package-${p.key}-order`} min={0} />
              {p.pricing === "flat" ? (
                <Num label="Prezzo fisso (€)" value={p.flat_price} onChange={(v) => setPkg(i, "flat_price", v)} testId={`cfg-package-${p.key}-flat`} step="0.01" min={0} />
              ) : (
                <Num label="Oltre (mq) → su misura" value={p.manual_over_sqm} onChange={(v) => setPkg(i, "manual_over_sqm", v)} testId={`cfg-package-${p.key}-manual-over`} min={1} />
              )}
            </div>
            {p.pricing !== "flat" && (
              <div className="space-y-2" data-testid={`cfg-package-${p.key}-bands`}>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Fasce di prezzo</Label>
                {(p.bands || []).map((b, j) => (
                  <div key={j} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid={`cfg-package-${p.key}-band-${j}`}>
                    <Num label="Fino a (mq)" value={b.max_sqm} min={1}
                      onChange={(v) => { const bands = p.bands.slice(); bands[j] = { ...b, max_sqm: v }; setPkg(i, "bands", bands); }}
                      testId={`cfg-package-${p.key}-band-${j}-sqm`} />
                    <Num label="Prezzo (€)" value={b.price} step="0.01" min={0}
                      onChange={(v) => { const bands = p.bands.slice(); bands[j] = { ...b, price: v }; setPkg(i, "bands", bands); }}
                      testId={`cfg-package-${p.key}-band-${j}-price`} />
                    <button onClick={() => setPkg(i, "bands", p.bands.filter((_, k) => k !== j))}
                      className="min-h-[44px] px-3 border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone transition-colors"
                      data-testid={`cfg-package-${p.key}-band-${j}-remove`} aria-label="Rimuovi fascia"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => setPkg(i, "bands", [...(p.bands || []), emptyBand()])}
                  className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid={`cfg-package-${p.key}-band-add`}>
                  <Plus size={14} /> Aggiungi fascia
                </button>
              </div>
            )}
            <Txt label="Modalità di pagamento (testo indicativo)" value={p.payment_text} onChange={(v) => setPkg(i, "payment_text", v)} testId={`cfg-package-${p.key}-payment`} textarea />
          </div>
        ))}
      </div>

      <div className="bg-white border border-hair p-5 md:p-6 space-y-4" data-testid="cfg-fiscal">
        <div className="eyebrow">Configurazione fiscale</div>
        <Toggle label="Configurazione fiscale completata" checked={cfg.fiscal?.configured}
          onChange={(v) => setFiscal("configured", v)} testId="cfg-fiscal-configured"
          hint="Attivala solo dopo aver verificato i valori: senza questa spunta il configuratore pubblico resta disattivato" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Num label="Contributo previdenziale (%)" value={cfg.fiscal?.contribution_percent} onChange={(v) => setFiscal("contribution_percent", v)} testId="cfg-fiscal-contribution" step="0.01" min={0} />
          <Num label="Aliquota IVA (%)" value={cfg.fiscal?.vat_rate} onChange={(v) => setFiscal("vat_rate", v)} testId="cfg-fiscal-vat-rate" step="0.01" min={0} />
          <div data-testid="cfg-fiscal-amounts-mode">
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Importi mostrati</Label>
            <Select value={cfg.fiscal?.amounts_mode || "oltre"} onValueChange={(v) => setFiscal("amounts_mode", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="cfg-fiscal-amounts-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oltre">Oltre contributi e imposte</SelectItem>
                <SelectItem value="comprensivi">Comprensivi di tutto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Toggle label="IVA applicata" checked={cfg.fiscal?.vat_enabled} onChange={(v) => setFiscal("vat_enabled", v)} testId="cfg-fiscal-vat-enabled" />
          <Toggle label="Imposta di bollo attiva" checked={cfg.fiscal?.bollo_enabled} onChange={(v) => setFiscal("bollo_enabled", v)} testId="cfg-fiscal-bollo-enabled" />
        </div>
        {cfg.fiscal?.bollo_enabled && (
          <div className="grid grid-cols-2 gap-4">
            <Num label="Importo bollo (€)" value={cfg.fiscal?.bollo_amount} onChange={(v) => setFiscal("bollo_amount", v)} testId="cfg-fiscal-bollo-amount" step="0.01" min={0} />
            <Num label="Soglia bollo (€)" value={cfg.fiscal?.bollo_threshold} onChange={(v) => setFiscal("bollo_threshold", v)} testId="cfg-fiscal-bollo-threshold" step="0.01" min={0} />
          </div>
        )}
        {!cfg.fiscal?.vat_enabled && (
          <Txt label="Testo regime fiscale (es. regime forfettario)" value={cfg.fiscal?.regime_text} onChange={(v) => setFiscal("regime_text", v)} testId="cfg-fiscal-regime" textarea />
        )}
        <Txt label="Note fiscali aggiuntive" value={cfg.fiscal?.fiscal_notes} onChange={(v) => setFiscal("fiscal_notes", v)} testId="cfg-fiscal-notes" textarea />
      </div>

      <button onClick={save} disabled={busy}
        className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="cfg-save-btn">
        <Save size={14} /> Salva configurazione
      </button>
    </div>
  );
}
